/**
 * useTrackImpression — generic impression tracker for all platform surfaces.
 *
 * Fires once on mount. Deduped via useRef keyed on entityId — prevents
 * double-fire in React StrictMode while still re-tracking if the entityId
 * changes (e.g. ad rotation, paginated lists).
 *
 * Return-visit semantics: the dedup ref resets when entityId becomes
 * undefined, so navigating A → none → A counts as two distinct impressions.
 * Without the reset, a user returning to the same entity within a session
 * would silently lose the second view.
 *
 * Usage:
 *   useTrackImpression(EntityType.hub, hub._id, TrackSurface.hub_page);
 *   useTrackImpression(EntityType.quest, questId, TrackSurface.quest_card);
 */

import {useRef, useEffect} from "react";
import {EntityType, TrackEvent, TrackSurface} from "@devour/client";
import analytics from "@/utils/analytics";

export function useTrackImpression(
    entityType: EntityType,
    entityId: string | undefined,
    surface: TrackSurface,
): void {
    const lastTrackedId = useRef<string | null>(null);

    useEffect(() => {
        if (!entityId) {
            lastTrackedId.current = null;
            return;
        }
        if (lastTrackedId.current !== entityId) {
            lastTrackedId.current = entityId;
            void analytics.track({entityType, entityId, event: TrackEvent.impression, surface});
        }
    }, [entityId, entityType, surface]);
}
